/**
 * Media Library Page Component
 * 
 * Main component for managing media files - matches Media Library.html design
 */
import { useState } from 'react';
import { useMediaLibrary, useMediaStats, useDeleteMedia, useUploadMedia, type MediaItem } from '@/hooks/use-media';
import { Upload, Search, Grid, List, Trash2, Eye, Image as ImageIcon, Video, File, Music, FileText, Bell, CloudUpload, X, HardDrive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

function getFileIcon(fileType: string) {
  switch (fileType) {
    case 'image':
      return ImageIcon;
    case 'video':
      return Video;
    case 'audio':
      return Music;
    case 'document':
      return FileText;
    default:
      return File;
  }
}

function getFileIconColor(fileType: string): { bg: string; icon: string } {
  switch (fileType) {
    case 'image':
      return { bg: 'bg-green-100', icon: 'text-green-600' };
    case 'video':
      return { bg: 'bg-purple-100', icon: 'text-purple-600' };
    case 'document':
      return { bg: 'bg-red-100', icon: 'text-red-600' };
    case 'audio':
      return { bg: 'bg-blue-100', icon: 'text-blue-600' };
    default:
      return { bg: 'bg-gray-100', icon: 'text-gray-600' };
  }
}

export function MediaLibraryPage() {
  const [page, setPage] = useState(1);
  const [fileType, setFileType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'uploaded_at' | 'file_size' | 'filename'>('uploaded_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const { data: mediaData, isLoading: mediaLoading, error: mediaError } = useMediaLibrary({
    page,
    pageSize: 24,
    fileType: fileType === 'all' ? undefined : fileType,
    search: searchQuery || undefined,
    sortBy,
    sortDir,
  });

  const { data: stats, isLoading: statsLoading, error: statsError } = useMediaStats();
  const deleteMedia = useDeleteMedia();
  const uploadMedia = useUploadMedia();

  // Debug logging (disabled to reduce console noise)
  // console.log('MediaLibraryPage render:', {
  //   mediaLoading,
  //   mediaError,
  //   hasMediaData: !!mediaData,
  //   mediaCount: mediaData?.media?.length || 0,
  //   statsLoading,
  //   statsError,
  //   hasStats: !!stats,
  // });

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      await uploadMedia.mutateAsync({ file: selectedFile });
      setUploadModalOpen(false);
      setSelectedFile(null);
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const handleDelete = async (mediaId: string) => {
    if (!confirm('Bạn có chắc chắn muốn xóa file này?')) return;

    try {
      await deleteMedia.mutateAsync(mediaId);
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Always render the component structure, even if data is loading or has errors
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your uploaded files and media assets</p>
          </div>
          <div className="flex items-center space-x-4">
            <button className="relative p-2 text-gray-400 hover:text-gray-600">
              <Bell className="h-5 w-5" />
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">3</span>
            </button>
            <Button
              variant="yellow"
              onClick={() => setUploadModalOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Media
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-gray-50">
        {/* Stats Cards */}
        {statsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-pulse">
                <div className="h-20"></div>
              </div>
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Files</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalFiles.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <File className="text-blue-600 text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Images</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.images.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <ImageIcon className="text-green-600 text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Videos</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.videos.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Video className="text-purple-600 text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Storage Used</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalStorageFormatted}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <HardDrive className="text-yellow-600 text-xl" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">—</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">0</p>
                  </div>
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <File className="text-gray-400 text-xl" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error Message */}
        {(mediaError || statsError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">
              {mediaError?.message || statsError?.message || 'Có lỗi xảy ra khi tải dữ liệu'}
            </p>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search media files..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent w-80"
                  />
                </div>
                <select
                  value={fileType}
                  onChange={(e) => setFileType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="image">Images</option>
                  <option value="video">Videos</option>
                  <option value="document">Documents</option>
                  <option value="audio">Audio</option>
                </select>
                <select
                  value={`${sortBy}-${sortDir}`}
                  onChange={(e) => {
                    const [by, dir] = e.target.value.split('-');
                    setSortBy(by as typeof sortBy);
                    setSortDir(dir as typeof sortDir);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="uploaded_at-desc">Upload Date</option>
                  <option value="file_size-desc">File Size</option>
                  <option value="filename-asc">File Name</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 border border-gray-300 rounded-lg transition-colors ${
                    viewMode === 'grid'
                      ? 'text-primary border-primary bg-primary/5'
                      : 'text-gray-600 hover:text-primary hover:border-primary'
                  }`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 border border-gray-300 rounded-lg transition-colors ${
                    viewMode === 'list'
                      ? 'text-primary border-primary bg-primary/5'
                      : 'text-gray-600 hover:text-primary hover:border-primary'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Media Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            {mediaLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : mediaData?.media && mediaData.media.length === 0 ? (
              <div className="text-center py-12">
                <File className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No media files found</p>
              </div>
            ) : mediaData?.media && mediaData.media.length > 0 ? (
              <>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {mediaData?.media.map((item: MediaItem) => (
                      <MediaCard key={item.id} item={item} onDelete={handleDelete} />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {mediaData?.media.map((item: MediaItem) => (
                      <MediaListItem key={item.id} item={item} onDelete={handleDelete} />
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {mediaData && mediaData.pagination.totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      Showing {(page - 1) * 24 + 1}-{Math.min(page * 24, mediaData.pagination.total)} of {mediaData.pagination.total} files
                    </p>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {Array.from({ length: Math.min(mediaData.pagination.totalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (mediaData.pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= mediaData.pagination.totalPages - 2) {
                          pageNum = mediaData.pagination.totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        return pageNum;
                      })
                        .filter((p, idx, arr) => arr.indexOf(p) === idx)
                        .map((p) => (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            className={`px-3 py-2 rounded-lg text-sm ${
                              p === page
                                ? 'bg-primary text-white'
                                : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      <button
                        onClick={() => setPage(p => Math.min(mediaData.pagination.totalPages, p + 1))}
                        disabled={page === mediaData.pagination.totalPages}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <File className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No media files found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {uploadModalOpen && (
        <UploadModal
          isOpen={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedFile(null);
          }}
          selectedFile={selectedFile}
          onFileSelect={handleFileSelect}
          onUpload={handleUpload}
          onDrag={handleDrag}
          onDrop={handleDrop}
          dragActive={dragActive}
          uploading={uploadMedia.isPending}
        />
      )}
    </div>
  );
}

function MediaCard({ item, onDelete }: { item: MediaItem; onDelete: (id: string) => void }) {
  const [hovered, setHovered] = useState(false);
  const FileIcon = getFileIcon(item.fileType);
  const iconColors = getFileIconColor(item.fileType);

  return (
    <div
      className="group relative bg-gray-100 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="aspect-square">
        {item.fileType === 'image' && item.url ? (
          <img
            src={item.url}
            alt={item.altText || item.originalFilename}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${iconColors.bg}`}>
            <FileIcon className={`h-12 w-12 ${iconColors.icon}`} />
          </div>
        )}
      </div>
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
        <div className="flex space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.open(item.url, '_blank');
            }}
            className="p-2 bg-white rounded-full text-gray-600 hover:text-primary transition-colors"
          >
            <Eye className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.id);
            }}
            className="p-2 bg-white rounded-full text-gray-600 hover:text-red-600 transition-colors"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div className="p-3">
        <p className="text-xs font-medium text-gray-900 truncate">{item.originalFilename}</p>
        <p className="text-xs text-gray-500">{formatFileSize(item.fileSize)}</p>
      </div>
    </div>
  );
}

function MediaListItem({ item, onDelete }: { item: MediaItem; onDelete: (id: string) => void }) {
  const FileIcon = getFileIcon(item.fileType);
  const iconColors = getFileIconColor(item.fileType);

  return (
    <div className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
      <div className="w-16 h-16 flex-shrink-0">
        {item.fileType === 'image' && item.url ? (
          <img
            src={item.url}
            alt={item.altText || item.originalFilename}
            className="w-full h-full object-cover rounded"
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center rounded ${iconColors.bg}`}>
            <FileIcon className={`h-8 w-8 ${iconColors.icon}`} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{item.originalFilename}</p>
        <p className="text-xs text-gray-500">{formatFileSize(item.fileSize)} • {item.fileType}</p>
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => window.open(item.url, '_blank')}
          className="p-2 text-gray-400 hover:text-primary transition-colors"
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(item.id)}
          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function UploadModal({
  isOpen,
  onClose,
  selectedFile,
  onFileSelect,
  onUpload,
  onDrag,
  onDrop,
  dragActive,
  uploading,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedFile: File | null;
  onFileSelect: (file: File) => void;
  onUpload: () => void;
  onDrag: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  dragActive: boolean;
  uploading: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Upload Media Files</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="p-6">
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-gray-300'
            }`}
            onDragEnter={onDrag}
            onDragLeave={onDrag}
            onDragOver={onDrag}
            onDrop={onDrop}
          >
            <CloudUpload className="text-4xl text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Drag and drop files here</h4>
            <p className="text-gray-500 mb-4">or click to browse your computer</p>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  onFileSelect(e.target.files[0]);
                }
              }}
              multiple={false}
            />
            <label htmlFor="file-upload">
              <Button variant="yellow" className="px-6 py-2 rounded-lg font-medium cursor-pointer">
                Choose Files
              </Button>
            </label>
            {selectedFile && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-900">Selected: {selectedFile.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={uploading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <Button
              variant="yellow"
              onClick={onUpload}
              disabled={!selectedFile || uploading}
              className="px-6 py-2 rounded-lg font-medium"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
